/* eslint-disable camelcase */
import { useContext, useState } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useMutation, api } from "../../../api"
import getIconComponent from "../../utils/getIconComponent"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../../utils/reactTableActions/exportToolbarActions"
import ModalImportCSV from "../../../components/CustomCSVImportButton"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const JuntasReceptoras = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)

  // ========================================================
  // LECTURA ROBUSTA DE PERMISOS (FALLBACK A LOCALSTORAGE)
  // ========================================================
  let barraAcciones = selectedMenuInfo?.data?.barraAcciones

  if (!barraAcciones || barraAcciones.length === 0) {
    try {
      const menuAccionesRaw = localStorage.getItem("menuAcciones")
      if (menuAccionesRaw) {
        const menuAcciones = JSON.parse(menuAccionesRaw)
        // Buscamos la ruta exacta de este nuevo módulo
        const currentMenu = menuAcciones["ControlElectoral/JuntasReceptoras"]
        if (currentMenu && currentMenu.barraAcciones) {
          barraAcciones = currentMenu.barraAcciones
        }
      }
    } catch (error) {
      console.error("Error leyendo permisos desde localStorage:", error)
    }
  }

  // ========================================================
  // MUTACIÓN PARA ELIMINAR REGISTRO
  // ========================================================
  const { mutateAsync: SaveEliminacionJunta, isPending: isDeletingJunta } = useMutation({
    queryKey: ["isDeletingJunta"],
    fn: async (data) => {
      // Apunta al endpoint exacto que creamos en rutas_maestros.py
      const response = await api.post("/ControlElectoral/eliminarJunta", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["JuntasReceptoras"] })
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Catálogo de Juntas Receptoras del Voto</b>
        </div>

        <CustomBackdrop isLoading={isDeletingJunta} />

        <Box className={StyledRoot}>
          {/* ======================================================== */}
          {/* MODAL DE IMPORTACIÓN CSV                                 */}
          {/* ======================================================== */}
          <ModalImportCSV
            open={openModal}
            onClose={() => setOpenModal(false)}
            templateFileName="PlantillaCreacionJuntas.csv"
            fieldConfigs={{
              junta_codigo: { required: true, key: true },
              reci_codigo: { required: true },
              junta_numero: { required: true },
              junta_genero: { required: true },
              junta_empadronados: { required: true },
            }}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/ControlElectoral/validarJuntasIMP"
            insertEndpoint="/ControlElectoral/insertarJuntasIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["JuntasReceptoras"] })}
          />

          {/* ======================================================== */}
          {/* TABLA PRINCIPAL DEL MÓDULO                               */}
          {/* ======================================================== */}
          <CustomConditionalActionsTableServer
            endpoint="/ControlElectoral/getAllJuntas"
            errorMsgFilterSearch="Error al cargar las juntas receptoras"
            queryKeyModal="JuntasReceptoras"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              const editarAction = barraAcciones?.find((action) => action?.acccaption === "EDITAR")
              const eliminarAction = barraAcciones?.find((action) => action?.acccaption === "ELIMINAR")

              const actions = [
                {
                  label: editarAction?.acccaption,
                  key: editarAction?.acccaption,
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => {
                    navigate("editar", { state: row.original })
                  },
                },
                {
                  label: eliminarAction?.acccaption,
                  key: eliminarAction?.acccaption,
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: async (row) => {
                    try {
                      // Se envía la PK correcta: junta_codigo
                      await SaveEliminacionJunta({
                        junta_codigo: row.original.junta_codigo,
                      })
                    } catch (err) {
                      console.error("Error eliminando la junta receptora:", err)
                    }
                  },
                },
              ]

              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = barraAcciones?.find((action) => action?.acccaption === "CREAR")
              const exportarAction = barraAcciones?.find((action) => action?.acccaption === "EXPORTAR")
              const importarAction = barraAcciones?.find((action) => action?.acccaption === "IMPORTAR")

              const toolbarActions = [
                {
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => {
                    navigate("crear")
                  },
                },
                {
                  type: "dropdown",
                  label: exportarAction?.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            "Reporte de Juntas Receptoras",
                            `Reporte de Juntas Receptoras ${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          "Reporte de Juntas Receptoras",
                          `Reporte de Juntas Receptoras ${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) => {
                        handleAllExportDataCSV(data, `Reporte de Juntas Receptoras ${new Date().toLocaleString()}`)
                      },
                    },
                  ],
                },
                {
                  label: importarAction?.acccaption,
                  key: importarAction?.acccaption,
                  icon: getIconComponent(importarAction?.accnameicono, importarAction?.acctipoico),
                  onClick: () => {
                    setOpenModal(true)
                  },
                },
              ]

              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "junta_codigo",
                header: "Código Junta",
                size: 140,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "reci_codigo",
                header: "Código Recinto",
                size: 140,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "junta_numero",
                header: "Nro. Junta",
                size: 100,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "junta_genero",
                header: "Género",
                size: 100,
                Cell: ({ cell }) => {
                  const val = cell.getValue()
                  return <span>{val === "M" ? "Masculino" : val === "F" ? "Femenino" : val}</span>
                },
              },
              {
                accessorKey: "junta_empadronados",
                header: "Empadronados",
                size: 130,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "aud_estisys",
                header: "Estado",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "aud_fecisys",
                header: "Fecha de Registro",
                size: 160,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "aud_usuisys",
                header: "Registrado Por",
                size: 150,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default JuntasReceptoras
